'use client';

import { useEffect } from 'react';
import { usePathname } from 'next/navigation';
import { trackCustomEvent } from '@/lib/datadog';

/**
 * Custom hook for tracking user interactions and page views with Datadog
 */
export const useDatadogTracking = () => {
  const pathname = usePathname();

  // Track page views
  useEffect(() => {
    if (pathname) {
      trackCustomEvent('page_view', {
        page_path: pathname,
        page_title: document.title,
        referrer: document.referrer,
      });
    }
  }, [pathname]);

  // Utility functions for tracking specific events
  const trackApiDocumentationView = (apiName: string, section?: string) => {
    trackCustomEvent('api_documentation_view', {
      api_name: apiName,
      section: section,
      page_path: pathname,
    });
  };

  const trackCodeExampleCopy = (language: string, apiEndpoint?: string) => {
    trackCustomEvent('code_example_copy', {
      language: language,
      api_endpoint: apiEndpoint,
      page_path: pathname,
    });
  };

  const trackSearchQuery = (query: string, resultsCount?: number) => {
    trackCustomEvent('search_query', {
      query: query,
      results_count: resultsCount,
      page_path: pathname,
    });
  };

  const trackNavigationClick = (linkText: string, destination: string) => {
    trackCustomEvent('navigation_click', {
      link_text: linkText,
      destination: destination,
      source_page: pathname,
    });
  };

  const trackExternalLinkClick = (url: string, linkText?: string) => {
    trackCustomEvent('external_link_click', {
      external_url: url,
      link_text: linkText,
      source_page: pathname,
    });
  };

  const trackParameterTableInteraction = (parameterName: string, action: 'expand' | 'collapse') => {
    trackCustomEvent('parameter_table_interaction', {
      parameter_name: parameterName,
      action: action,
      page_path: pathname,
    });
  };

  const trackUseCaseView = (useCaseName: string, industry?: string) => {
    trackCustomEvent('use_case_view', {
      use_case_name: useCaseName,
      industry: industry,
      page_path: pathname,
    });
  };

  return {
    trackApiDocumentationView,
    trackCodeExampleCopy,
    trackSearchQuery,
    trackNavigationClick,
    trackExternalLinkClick,
    trackParameterTableInteraction,
    trackUseCaseView,
  };
}; 